import React, { useEffect, useMemo, useState } from 'react'
import {
  Paper,
  Chip,
  Menu,
  MenuItem,
  IconButton,
  Button,
  Typography,
  Box,
  makeStyles,
  FormControl,
  Select
} from '@material-ui/core'
import { useHistory, useLocation } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import ResourceNotFound from 'components/ResourceNotFound'
import {
  EditOutlined as EditIcon,
  DeleteOutlineOutlined as DeleteIcon,
  EmailOutlined as EmailIcon,
  MoreVert as MoreVertIcon,
  ContentCopy,
  People as PeopleIcon,
  Close as CloseIcon,
  KeyboardArrowRight,
  CheckCircleOutline
} from '@material-ui/icons'
import getTableOptions from 'utils/getTableOptions'
import ConfirmModal from 'components/ConfirmationModal'
import BasicTable from 'components/BasicTable'
import { renderCol } from 'utils/renderColumns'
import { caseInsensitiveSort } from 'utils'
import { convertTimestampToLocalString } from 'utils/times'
import { organizationStatus } from 'constants/index'
import { cloudFunctionsInstance } from 'config/axios'
import { toast } from 'utils/notify'
import organizationSlice, {
  approveOrganizationById,
  deleteOrganizationById,
  getOrganizations,
  getSponsorshipLevels
} from 'store/organization'
import DataLoading from 'components/DataLoading'

import BulkImportOrganizationsModal from 'views/admin/organizations/components/BulkImportOrganizationsModal'
import ImportOrgRepresentativesModal from 'views/admin/organizations/components/ImportOrgRepresentativesModal'
import { chipStyleForOrganization } from './utils'

const OrganizationListing = () => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const history = useHistory()
  const location = useLocation()
  const [modalOpen, setModalOpen] = useState(false)
  const [bulkModalOpen, setBulkModalOpen] = useState(false)
  const [selectedOrganization, setSelectedOrganization] = useState(null)
  const [sendingEmail, setSendingEmail] = useState(false)
  const [menuAnchorEl, setMenuAnchorEl] = useState(null)
  const [currentOrganization, setCurrentOrganization] = useState(null)
  const [isBulkOperation, setIsBulkOperation] = useState(false)

  const [loading, setLoading] = useState(false)
  const [deletedSponsorId, setDeletedSponsorId] = useState(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [importModalOpen, setImportModalOpen] = useState(false)
  const [importRepresentativesModalOpen, setImportRepresentativesModalOpen] = useState(false)
  const [isApproving, setIsApproving] = useState(false)

  const [confSponsorshipLevels, setConfSponsorshipLevels] = useState([])

  const { user } = useSelector((state) => state.auth)
  const { conference } = useSelector((state) => state.conference)
  const { organizations, statusFilter, typeFilter, currentPage, pageSize } = useSelector(
    (state) => state.organization
  )

  const handleApprove = async (org) => {
    setMenuAnchorEl(null)
    setIsApproving(true)
    try {
      await approveOrganizationById(org.id)
      toast.success('Organization approved.')
    } catch (err) {
      toast.error('Could not approve organization. Try again.')
    } finally {
      setIsApproving(false)
    }
  }

  const handleCloseImportModal = () => setImportModalOpen(false)

  const openImportModal = () => {
    setImportModalOpen(true)
  }

  const setStatusFilter = (status) => {
    dispatch(organizationSlice.actions.setStatusFilter(status))
  }

  const setTypeFilter = (type) => {
    dispatch(organizationSlice.actions.setTypeFilter(type))
  }

  const clearFilters = () => {
    setStatusFilter('all')
    setTypeFilter('all')
  }

  const orgStats = useMemo(() => {
    const stats = {
      [organizationStatus.INCOMPLETE]: 0,
      [organizationStatus.INVITE_NOT_SENT]: 0,
      [organizationStatus.AWAITING_RESPONSE]: 0,
      [organizationStatus.PENDING_DATA_REVIEW]: 0,
      [organizationStatus.APPROVED]: 0,
      exhibitor: 0,
      sponsor: 0
    }

    organizations?.forEach((org) => {
      // Count exhibitors and sponsors
      const sponsorshipLevel = confSponsorshipLevels.find(
        (level) => level.id === org.sponsorshipLevelId
      )
      if (sponsorshipLevel) {
        const isExhibitor = sponsorshipLevel.sponsorshipTier === 1
        if (isExhibitor) {
          stats.exhibitor++
        } else {
          stats.sponsor++
        }
      }
    })

    return stats
  }, [organizations, confSponsorshipLevels])

  const totalCount = useMemo(() => {
    return organizations?.length
  }, [organizations])

  const organizationsWithSponsorshipLevels = useMemo(() => {
    return organizations?.map((org) => {
      const sponsorshipLevel = confSponsorshipLevels.find(
        (level) => level.id === org.sponsorshipLevelId
      )
      return {
        ...org,
        sponsorshipLevel: sponsorshipLevel
      }
    })
  }, [confSponsorshipLevels, organizations])

  const confTimezone = conference?.timezone
  const conf24HrFormat = conference?.is24hrFormat

  useEffect(() => {
    const fetchLevels = async () => {
      setLoading(true)
      const levels = await getSponsorshipLevels(user?.conferenceId)
      setConfSponsorshipLevels(levels)
      setLoading(false)
    }

    fetchLevels()
  }, [])

  useEffect(() => {
    const unsub = getOrganizations(user?.conferenceId, (data) => {
      dispatch(
        organizationSlice.actions.setOrganizations(caseInsensitiveSort(data, 'organization'))
      )
    })
    return () => unsub && unsub()
  }, [dispatch, user?.conferenceId])

  const handleConfirmDelete = async () => {
    try {
      setIsDeleting(true)
      const res = await deleteOrganizationById(deletedSponsorId, user?.conferenceId)
      res && toast.success('Organization deleted.')
    } catch (err) {
      toast.error('An error occurred while deleting the organization. Please try again.')
    } finally {
      setIsDeleting(false)
    }
  }

  const handleCloseDeleteModal = () => {
    setDeletedSponsorId(null)
  }

  const tableHead = [
    'Organization',
    'Level',
    'Booth No',
    'Invite Sent On',
    'Data Received On',
    'Approved On',
    {
      name: 'Status',
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value) => renderCol(value),
        customHeadLabelRender: (meta) => renderCol(meta.label)
      }
    },
    {
      name: 'Actions',
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value) => renderCol(value),
        customHeadLabelRender: (meta) => (
          <div style={{ textAlign: 'center' }}>{renderCol(meta.label)}</div>
        )
      }
    }
  ]
  const formatTableData = (orgs) =>
    orgs?.map((org) => [
      org?.organization || 'N/A',
      org?.sponsorshipLevel?.tierName || 'N/A',
      org?.booth || 'N/A',
      org?.remindedAt || org?.invitedAt
        ? convertTimestampToLocalString(
            org?.remindedAt || org?.invitedAt,
            confTimezone,
            conf24HrFormat
          )
        : 'N/A',
      org?.submittedAt
        ? convertTimestampToLocalString(org?.submittedAt, confTimezone, conf24HrFormat)
        : 'N/A',
      org?.approvedAt
        ? convertTimestampToLocalString(org?.approvedAt, confTimezone, conf24HrFormat)
        : 'N/A',
      <Chip
        label={org?.status || organizationStatus.INVITE_NOT_SENT}
        style={{
          ...chipStyleForOrganization(org?.status || organizationStatus.INVITE_NOT_SENT).style
        }}
      />,
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <IconButton
          size="small"
          onClick={(event) => {
            setMenuAnchorEl(event.currentTarget)
            setCurrentOrganization(org)
          }}>
          <MoreVertIcon />
        </IconButton>
        <Menu
          anchorEl={menuAnchorEl}
          open={Boolean(menuAnchorEl) && currentOrganization?.id === org?.id}
          onClose={() => {
            setMenuAnchorEl(null)
          }}>
          <MenuItem
            onClick={() => {
              setMenuAnchorEl(null)
              history.push(`${location.pathname}/edit/${org?.id}`)
            }}>
            <EditIcon color="action" fontSize="small" style={{ marginRight: 10 }} />
            Edit
          </MenuItem>

          {/* Add Representatives direct link */}
          <MenuItem
            onClick={() => {
              setMenuAnchorEl(null)
              history.push(`${location.pathname}/edit/${org?.id}?tab=representatives`)
            }}>
            <PeopleIcon fontSize="small" color="action" style={{ marginRight: 10 }} />
            View Representatives
          </MenuItem>

          {org?.status !== organizationStatus.APPROVED && (
            <MenuItem onClick={() => handleApprove(org)} disabled={isApproving}>
              <CheckCircleOutline color="action" fontSize="small" style={{ marginRight: 10 }} />
              Mark as Approved
            </MenuItem>
          )}

          {(org?.status === organizationStatus.INVITE_NOT_SENT ||
            org?.status === organizationStatus.AWAITING_RESPONSE) && (
            <MenuItem
              onClick={() => {
                setMenuAnchorEl(null)
                setSelectedOrganization(org)
                setModalOpen(true)
              }}>
              <EmailIcon color="action" fontSize="small" style={{ marginRight: 10 }} />
              {org?.status === organizationStatus.INVITE_NOT_SENT
                ? 'Send Invitation'
                : 'Send Reminder'}
            </MenuItem>
          )}

          {org?.status !== organizationStatus.INCOMPLETE &&
            org?.status !== organizationStatus.INVITE_NOT_SENT &&
            org?.invitedAt && (
              <MenuItem
                onClick={() => {
                  setMenuAnchorEl(null)
                  navigator.clipboard.writeText(
                    `${window.location.origin}/organization-profile?token=${org?.token}`
                  )
                  toast.info('Invite URL copied to clipboard', {
                    duration: 1000,
                    isClosable: true
                  })
                }}>
                <ContentCopy fontSize="small" color="action" style={{ marginRight: 10 }} />
                Copy Invite URL
              </MenuItem>
            )}

          <MenuItem
            onClick={() => {
              setMenuAnchorEl(null)
              setDeletedSponsorId(org?.id)
            }}>
            <DeleteIcon color="action" fontSize="small" style={{ marginRight: 10 }} />
            Delete
          </MenuItem>
        </Menu>
      </div>
    ])

  const options = getTableOptions()

  // Create type-filtered organizations list first
  const typeFilteredOrgs = useMemo(() => {
    if (typeFilter === 'all') {
      return organizationsWithSponsorshipLevels
    }

    return organizationsWithSponsorshipLevels.filter((org) => {
      const isExhibitor = org?.sponsorshipLevel?.sponsorshipTier === 1
      return typeFilter === 'exhibitor' ? isExhibitor : !isExhibitor
    })
  }, [organizationsWithSponsorshipLevels, typeFilter])

  // Calculate stats for status filters based on current type filter
  const statusStats = useMemo(() => {
    const stats = {
      [organizationStatus.INCOMPLETE]: 0,
      [organizationStatus.INVITE_NOT_SENT]: 0,
      [organizationStatus.AWAITING_RESPONSE]: 0,
      [organizationStatus.PENDING_DATA_REVIEW]: 0,
      [organizationStatus.APPROVED]: 0
    }

    typeFilteredOrgs?.forEach((org) => {
      if (Object.prototype.hasOwnProperty.call(stats, org.status)) {
        stats[org.status]++
      }
    })

    return stats
  }, [typeFilteredOrgs])

  // Filter organizations based on status and type
  const filteredOrganizations = useMemo(() => {
    let filtered = typeFilteredOrgs

    // Filter by status
    if (statusFilter !== 'all') {
      filtered = filtered.filter((org) => org?.status === statusFilter)
    }

    return filtered
  }, [typeFilteredOrgs, statusFilter])

  const handleRedirect = (subpath) => {
    history.push(`${location.pathname}/${subpath}`)
  }

  const getNoResultsMessage = (statusFilter, typeFilter) => {
    if (statusFilter !== 'all' && typeFilter !== 'all') {
      const typeText = typeFilter === 'exhibitor' ? 'Exhibitor' : 'Sponsor'
      return `No ${typeText} organization with status "${statusFilter}" available`
    } else if (statusFilter !== 'all') {
      return `No organization with status "${statusFilter}" available`
    } else if (typeFilter !== 'all') {
      return `No ${typeFilter} organizations available`
    }
    return 'No organization available'
  }

  // Function to handle bulk operation
  const handleBulkOperation = async () => {
    setIsBulkOperation(true)
    setSendingEmail(true)
    try {
      await cloudFunctionsInstance.post('/inviteOrRemindOrganizationsViaEmail', {
        conferenceId: user?.conferenceId,
        reason: statusFilter === organizationStatus.INVITE_NOT_SENT ? 'invitation' : 'reminder'
      })
      toast.success(
        statusFilter === organizationStatus.INVITE_NOT_SENT
          ? 'Invitation emails sent to all organizations.'
          : 'Reminder emails sent to all waiting organizations.'
      )
    } catch (err) {
      const errorMessage =
        err?.response?.data?.msg || 'Failed to send emails, Please try again later'
      toast.error(errorMessage)
    } finally {
      setSendingEmail(false)
      setBulkModalOpen(false)
      setIsBulkOperation(false)
    }
  }

  if (loading) {
    return (
      <div style={{ marginTop: '2rem' }}>
        <DataLoading />
      </div>
    )
  }

  return (
    <>
      <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5" className={classes.pageTitle}>
          Organizations
        </Typography>
        <Box className={classes.buttonGroupBox}>
          <Button
            variant="contained"
            color="primary"
            size="large"
            endIcon={<KeyboardArrowRight />}
            onClick={() => handleRedirect('new')}>
            Create
          </Button>
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={() => openImportModal()}
            endIcon={<KeyboardArrowRight />}>
            Import Organizations via CSV
          </Button>
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={() => setImportRepresentativesModalOpen(true)}
            endIcon={<KeyboardArrowRight />}>
            Import Representatives via CSV
          </Button>
        </Box>
      </Box>

      <Paper variant="outlined" style={{ padding: '16px', marginTop: 16 }}>
        <Box style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
          <Box display="flex" alignItems="center">
            <Typography variant="body1" style={{ marginRight: 8 }}>
              Filter by Type:
            </Typography>
            <FormControl variant="outlined" size="small" style={{ minWidth: 200 }}>
              <Select
                id="filter-by-type"
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                displayEmpty
                style={{ height: 36 }}>
                <MenuItem value="all">All ({totalCount})</MenuItem>
                <MenuItem value="exhibitor">Exhibitor ({orgStats.exhibitor})</MenuItem>
                <MenuItem value="sponsor">Sponsor ({orgStats.sponsor})</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Box display="flex" alignItems="center">
            <Typography variant="body1" style={{ marginRight: 8 }}>
              Filter by Status:
            </Typography>
            <FormControl variant="outlined" size="small" style={{ minWidth: 200 }}>
              <Select
                id="filter-by-status"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                displayEmpty
                style={{ height: 36 }}>
                <MenuItem value="all">All ({typeFilteredOrgs.length})</MenuItem>
                <MenuItem value={organizationStatus.INCOMPLETE}>
                  Incomplete ({statusStats[organizationStatus.INCOMPLETE]})
                </MenuItem>
                <MenuItem value={organizationStatus.INVITE_NOT_SENT}>
                  Invite Not Sent ({statusStats[organizationStatus.INVITE_NOT_SENT]})
                </MenuItem>
                <MenuItem value={organizationStatus.AWAITING_RESPONSE}>
                  Awaiting Response ({statusStats[organizationStatus.AWAITING_RESPONSE]})
                </MenuItem>
                <MenuItem value={organizationStatus.PENDING_DATA_REVIEW}>
                  Pending Review ({statusStats[organizationStatus.PENDING_DATA_REVIEW]})
                </MenuItem>
                <MenuItem value={organizationStatus.APPROVED}>
                  Approved ({statusStats[organizationStatus.APPROVED]})
                </MenuItem>
              </Select>
            </FormControl>
          </Box>

          {(statusFilter !== 'all' || typeFilter !== 'all') && (
            <Button
              variant="outlined"
              color="primary"
              size="small"
              onClick={clearFilters}
              startIcon={<CloseIcon />}
              style={{ marginLeft: 'auto' }}>
              Clear
            </Button>
          )}
        </Box>
      </Paper>

      <Paper variant="outlined" style={{ marginTop: 16 }}>
        {organizations && organizations?.length ? (
          <>
            {filteredOrganizations.length > 0 ? (
              <div>
                {/* Conditional button for bulk operations */}
                {(statusFilter === organizationStatus.INVITE_NOT_SENT ||
                  statusFilter === organizationStatus.AWAITING_RESPONSE) && (
                  <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<EmailIcon />}
                      onClick={() => setBulkModalOpen(true)}
                      disabled={sendingEmail}>
                      {statusFilter === organizationStatus.INVITE_NOT_SENT
                        ? 'Send Invitations to All'
                        : 'Send Reminders to All Waiting for Data'}
                    </Button>
                  </div>
                )}
                <BasicTable
                  data={formatTableData(filteredOrganizations)}
                  columns={tableHead}
                  options={{
                    ...options,
                    onChangePage: (page) =>
                      dispatch(organizationSlice.actions.setCurrentPage(page)),
                    onChangeRowsPerPage: (size) =>
                      dispatch(organizationSlice.actions.setPageSize(size)),
                    page: currentPage,
                    rowsPerPage: pageSize
                  }}
                />
              </div>
            ) : (
              <ResourceNotFound message={getNoResultsMessage(statusFilter, typeFilter)} />
            )}
          </>
        ) : (
          <ResourceNotFound message="No organization available" />
        )}
      </Paper>

      {/* Confirmation Modal */}
      {/* Single Organization Email Confirmation Modal */}
      <ConfirmModal
        open={modalOpen}
        closeModal={() => {
          setModalOpen(false)
          setSelectedOrganization(null)
        }}
        dialogTitle={
          selectedOrganization?.status === organizationStatus.INVITE_NOT_SENT
            ? 'Send Invitation Email'
            : 'Send Reminder Email'
        }
        dialogText={
          selectedOrganization?.status === organizationStatus.INVITE_NOT_SENT
            ? `Are you sure you want to send an invitation email to ${selectedOrganization?.organization}?`
            : `Are you sure you want to send a reminder email to ${selectedOrganization?.organization}?`
        }
        inProgress={sendingEmail && !isBulkOperation}
        handleConfirm={async () => {
          setSendingEmail(true)
          try {
            await cloudFunctionsInstance.post('/inviteOrRemindOrgViaEmail', {
              organizationId: selectedOrganization.id,
              conferenceId: user?.conferenceId
            })
            toast.success(
              selectedOrganization?.status === organizationStatus.INVITE_NOT_SENT
                ? 'Organization invitation email sent.'
                : 'Organization reminder email sent.'
            )
          } catch (err) {
            const errorMessage =
              err?.response?.data?.msg || 'Failed to send email, Please try again later'
            toast.error(errorMessage)
          } finally {
            setSendingEmail(false)
            setModalOpen(false)
            setSelectedOrganization(null)
          }
        }}
      />

      {/* Bulk Email Confirmation Modal */}
      <ConfirmModal
        open={bulkModalOpen}
        closeModal={() => {
          setBulkModalOpen(false)
        }}
        dialogTitle={
          statusFilter === organizationStatus.INVITE_NOT_SENT
            ? 'Send Bulk Invitation Emails'
            : 'Send Bulk Reminder Emails'
        }
        dialogText={
          statusFilter === organizationStatus.INVITE_NOT_SENT
            ? `Are you sure you want to send invitation emails to all ${filteredOrganizations.length} organization(s) ?`
            : `Are you sure you want to send reminder emails to all ${filteredOrganizations.length} organization(s) waiting for data?`
        }
        inProgress={sendingEmail && isBulkOperation}
        handleConfirm={handleBulkOperation}
      />

      {/* Confirm Delete PublicOrganizationForm Modal */}
      <ConfirmModal
        dialogTitle="Delete Organization"
        dialogText="Do you really want to delete this organization?"
        dialogText2="This action will delete all associated representatives, if any. Please confirm to proceed."
        open={!!deletedSponsorId}
        closeModal={handleCloseDeleteModal}
        handleConfirm={handleConfirmDelete}
        inProgress={isDeleting}
      />

      <BulkImportOrganizationsModal open={importModalOpen} onClose={handleCloseImportModal} />
      <ImportOrgRepresentativesModal
        open={importRepresentativesModalOpen}
        onClose={() => setImportRepresentativesModalOpen(false)}
      />
    </>
  )
}

export default OrganizationListing

const useStyles = makeStyles((theme) => ({
  tabs: {
    marginTop: '20px',
    '& .MuiTab-root.MuiTab-labelIcon': {
      minHeight: '35px'
    },
    '& .MuiTab-root svg': {
      height: '20px',
      width: '20px',
      marginBottom: '0px !important'
    },
    '& .MuiTab-root .MuiTab-wrapper': {
      display: 'flex',
      flexDirection: 'row',
      gap: '8px',
      alignItems: 'center'
    },
    '& .MuiTab-root.Mui-selected': {
      color: theme.palette.primary.main,
      backgroundColor: 'rgba(51,220,235,0.16)'
    }
  },
  pageTitle: {
    fontWeight: 600,
    fontSize: '26px'
  },
  buttonGroupBox: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-start',
    '& .MuiButtonGroup-root': {
      gap: '8px'
    }
  },
  filterStatsBar: {
    margin: '12px',
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
    padding: '12px 16px',
    backgroundColor: 'rgba(0, 0, 0, 0.02)',
    borderRadius: '8px',
    border: '1px solid rgba(0, 0, 0, 0.04)'
  },
  statsChips: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  statsSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  statChipsContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px'
  },
  statChip: {
    fontSize: '15px',
    padding: '4px 8px',
    letterSpacing: '0.5px',
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.05)'
    }
  },
  activeChip: {
    fontSize: '15px',
    letterSpacing: '0.5px',
    padding: '4px 8px',
    backgroundColor: `${theme?.palette?.primary?.main || '#1976d2'} !important`,
    color: 'white',
    fontWeight: 'bold',
    '&:hover': {
      backgroundColor: theme?.palette?.primary?.dark || '#115293'
    }
  },
  clearButton: {
    whiteSpace: 'nowrap'
  }
}))
